import { noop } from 'lodash/fp';
import { DateTime } from 'luxon';
import { ReactNode } from 'react';
import { SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import TableLayout, {
  TableActionTD,
  TableRemoveRow,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { stringifyLatitude, stringifyLongitude } from 'dashboard/lib/utils/stringFormat';
import { userDisplayName } from 'dashboard/lib/valueDisplayers/userDisplayName';
import { ListItem, ListItemGeolocation, ListItemStatus } from 'dashboard/models/list-item';
import { User } from 'dashboard/models/user';
import Sticker, { StickerProps } from 'design-system/components/Sticker';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';
import { Place } from 'lib/interfaces/place';

const LIST_ITEM_STATUS_MAP: Record<ListItemStatus, StickerProps['variant']> = {
  [ListItemStatus.Pending]: 'caution',
  [ListItemStatus.Active]: 'success',
  [ListItemStatus.Archived]: 'caution',
};

const LIST_ITEM_STATUS_LABEL_MAP: Record<ListItemStatus, string> = {
  [ListItemStatus.Pending]: 'Pending',
  [ListItemStatus.Active]: 'Active',
  [ListItemStatus.Archived]: 'Archived',
};

const GeolocationListItemsTableContainer = styled('div')`
  margin-bottom: ${SPACINGS.base}px;
`;

interface GeolocationListItemsTableProps {
  sortParams: SortParams;
  listActive: boolean;
  isLoading: boolean;
  users: User[];
  listItems: ListItemGeolocation[];
  dataTest?: string;
  removeListItem: (listItem: ListItem) => void;
  handleTableRowOnMouseOver: (place: Place) => void;
  handleTableRowOnMouseOut: (place: Place) => void;
  handleTableRowClicked: (place: Place) => void;
  handleSortUpdate: (sortParams: SortParams) => void;
  nullState: ReactNode;
}

const GeolocationListItemsTable = ({
  sortParams,
  listActive,
  isLoading,
  users,
  listItems,
  dataTest,
  removeListItem,
  handleTableRowClicked,
  handleTableRowOnMouseOver,
  handleTableRowOnMouseOut,
  handleSortUpdate,
  nullState,
}: GeolocationListItemsTableProps) => {
  const handleColumnHeaderClick = (columnName: string) => {
    handleSortUpdate({
      sort: columnName,
      shouldSortDescending:
        columnName === sortParams.sort ? !sortParams.shouldSortDescending : true,
    });
  };
  const headers = [
    { key: 'latitude', label: 'Location', sortable: true },
    { key: 'radius-meters', label: 'Radius (meters)', sortable: true },
    { key: 'match-count', label: '# Matches', sortable: true },
    { key: 'creator', label: 'Added by', sortable: false },
    { key: 'created-at', label: 'Created at', sortable: true, timezone: true },
    { key: 'status', label: 'Status', sortable: true },
    { key: 'archive', label: '', sortable: false },
  ].map((column, i) => {
    return (
      <TableTH
        id={`geolocation-table__header--${column.key}`}
        key={i}
        canSort={column.sortable}
        isDescending={sortParams.shouldSortDescending}
        isSorted={sortParams.sort === column.key}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        onClick={column.sortable ? () => handleColumnHeaderClick(column.key) : null}
      >
        {column.label}
      </TableTH>
    );
  });

  const geolocationRows = listItems.map((geolocation, key) => {
    const creator =
      geolocation.relationships.creator.data != null
        ? (users.find(
            (apiModel) => apiModel.id === geolocation.relationships.creator.data.id,
          ) as User)
        : undefined;
    return (
      <GeolocationListItemsTableRow
        key={key}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        creator={creator}
        listActive={listActive}
        geolocation={geolocation}
        removeListItem={removeListItem}
        handleTableRowOnMouseOver={handleTableRowOnMouseOver}
        handleTableRowOnMouseOut={handleTableRowOnMouseOut}
        handleTableRowClicked={handleTableRowClicked}
      />
    );
  });

  return (
    <GeolocationListItemsTableContainer data-test={dataTest}>
      <TableLayout
        dataTest='geolocation-table__table'
        isLoading={isLoading}
        isEmpty={listItems.length === 0}
        nullState={nullState}
        showHeader={true}
        headerTR={<>{headers}</>}
      >
        {geolocationRows}
      </TableLayout>
    </GeolocationListItemsTableContainer>
  );
};

export default GeolocationListItemsTable;

interface GeolocationListItemsTableRowProps {
  creator: User;
  listActive: boolean;
  geolocation: ListItemGeolocation;
  removeListItem: (listItem: ListItem) => void;
  handleTableRowOnMouseOver: (place: Place) => void;
  handleTableRowOnMouseOut: (place: Place) => void;
  handleTableRowClicked: (place: Place) => void;
}

const GeolocationListItemsTableRow = ({
  creator,
  listActive,
  geolocation,
  removeListItem,
  handleTableRowOnMouseOver,
  handleTableRowOnMouseOut,
  handleTableRowClicked,
}: GeolocationListItemsTableRowProps) => {
  const {
    status,
    'created-at': createdAt,
    latitude: latitude,
    longitude: longitude,
    'radius-meters': radiusMeters,
    'match-count': matchCount,
    'redacted-at': redactedAt,
  } = geolocation.attributes;

  // NOTE: list items imported via API will not have a creator
  const nameFull = userDisplayName(creator);

  const handleClick =
    latitude && longitude && radiusMeters
      ? () =>
          handleTableRowClicked({
            lat: latitude,
            lng: longitude,
            radiusMeters: radiusMeters,
          })
      : noop;

  const handleMouseOver =
    latitude && longitude && radiusMeters
      ? () =>
          handleTableRowOnMouseOver({
            lat: latitude,
            lng: longitude,
            radiusMeters: radiusMeters,
          })
      : noop;

  const handleMouseOut =
    latitude && longitude && radiusMeters
      ? () =>
          handleTableRowOnMouseOut({
            lat: latitude,
            lng: longitude,
            radiusMeters: radiusMeters,
          })
      : noop;

  return (
    <TableTR
      data-test={`geolocation-table__row--${geolocation.id}`}
      id='geolocation-table__row'
      onClick={handleClick}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
    >
      <TableTD>
        {redactedAt ? (
          <Sticker id={geolocation.id}>Redacted</Sticker>
        ) : (
          `${stringifyLatitude(latitude ?? 0)}, ${stringifyLongitude(longitude ?? 0)}`
        )}
      </TableTD>
      {/* TODO: Andrew handle radius units display*/}
      <TableTD>
        {redactedAt ? <Sticker id={geolocation.id}>Redacted</Sticker> : `${radiusMeters}m`}
      </TableTD>
      <TableTD>{matchCount}</TableTD>
      <TableTD>{nameFull}</TableTD>
      <TableTD>{createdAt && DateTime.fromISO(createdAt).toFormat(DATE_AND_TIME_FORMAT)}</TableTD>
      <TableTD>
        {status && (
          <Sticker variant={LIST_ITEM_STATUS_MAP[status]}>
            {LIST_ITEM_STATUS_LABEL_MAP[status]}
          </Sticker>
        )}
      </TableTD>
      {listActive && status === ListItemStatus.Active ? (
        <TableActionTD>
          <TableRemoveRow
            onClick={() => removeListItem(geolocation)}
            id={`geolocation-table-row__delete--${geolocation.id}`}
            data-test={`geolocation-table-row__delete--${geolocation.id}`}
          />
        </TableActionTD>
      ) : (
        <TableTD />
      )}
    </TableTR>
  );
};
